import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useRouter,
} from "@tanstack/react-router";
import { minBy } from "lodash-es";
import { useEffect } from "react";
import { useTranslations } from "use-intl";

import { Image } from "@/components/Image/Image";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { HotelBookingHeader } from "@/features/hotels/components/hotel/HotelBookingHeader";
import { HotelOfferError } from "@/features/hotels/components/hotel/HotelOfferError";
import { HotelName } from "@/features/hotels/components/hotels/HotelName";
import { MayLikeHotelsList } from "@/features/hotels/components/hotels-list/MayLikeHotelsList";
import { useOptionalHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { HotelOffersContextProvider } from "@/features/hotels/contexts/HotelOffersContext";
import { hotelOfferQueryOptions } from "@/features/hotels/hooks/useHotelOffer";
import { useHotelSearch } from "@/features/hotels/hooks/useHotelSearch";
import { useRouteConfig } from "@/hooks/useRouteConfig";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

export const Route = createFileRoute(
  "/_authenticated/_hotels/_hotel-offer/hotels/$id",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id: hotelId } = Route.useParams();
  const { history } = useRouter();
  const matches = useMatchRoute();
  const routeConfig = useRouteConfig();
  const t = useTranslations();

  const { data: hotel, setData } = useOptionalHotelDataContext();
  const {
    searchResultKey,
    searchResultToken,
    progress,
    isFetching: isFetchingSearchData,
  } = useHotelSearch();

  const {
    data,
    isFetching: isFetchingOffers,
    isError,
    error,
    isFetched,
    isPending: isPendingOffers,
  } = useQuery(
    hotelOfferQueryOptions(
      searchResultKey &&
        searchResultToken &&
        !isFetchingSearchData &&
        progress === 100
        ? {
            hotelId,
            searchResultKey,
            searchResultToken,
          }
        : null,
    ),
  );

  // TODO: the below is a hack to update the price shown across multiple steps
  // it's possible that it won't be needed after revamp
  const minOfferPrice = isFeatureEnabled(Feature.HotelsPersistentSearch)
    ? minBy(data, (offer) => offer.price?.value)?.price
    : null;

  useEffect(() => {
    if (!minOfferPrice) {
      return;
    }

    setData((prev) => {
      if (!prev) {
        return null;
      }

      return {
        ...prev,
        minPrice: minOfferPrice,
      };
    });
  }, [minOfferPrice, setData]);

  if (isFetchingSearchData || isFetchingOffers || isPendingOffers) {
    return (
      <PageLoader>
        <AnimatedLoader icon="buildings" />
      </PageLoader>
    );
  }

  // user has changed search criteria when on offer screen
  const hasOfferExpired = isFetched && !data?.length;

  // it would be great if we could display expiration message but we have no way to differentiate
  // that from regular error, as all we're getting is Internal Server Error 500
  // if (hasOfferExpired) {
  //   return <OfferNotAvailableError variant="hotel" />;
  // }

  if (isError && !hasOfferExpired) {
    return <HotelOfferError error={error} />;
  }

  const isGallery = routeConfig.hotelStep?.presentationMode === "modal";
  const isRoomGallery = matches({
    to: "/hotels/$id/rooms/$roomToken/images",
  });

  const isFirstStepRoute = matches({
    from: "/",
    to: "/hotels/$id/details",
  });
  const isLastStepRoute = matches({
    from: "/",
    to: "/hotels/$id/confirmation",
  });

  const room =
    isRoomGallery !== false && data
      ? data
          .flatMap((offer) => offer.rooms.flatMap((room) => room.options))
          .find((option) => option.roomToken === isRoomGallery.roomToken)
      : null;

  return (
    <HotelOffersContextProvider data={data || []}>
      {!isLastStepRoute ? (
        <HotelBookingHeader
          title={
            isGallery ? (
              <div className="flex items-center justify-center gap-2">
                <h3 className="font-medium text-text-title">
                  {room ? room.name : hotel?.name}
                </h3>
              </div>
            ) : null
          }
          {...(routeConfig.hotelStep &&
          routeConfig.hotelStep.presentationMode !== "modal"
            ? { step: routeConfig.hotelStep.stepNumber, showStepper: true }
            : { showStepper: false })}
          showSearchForm={!!isFirstStepRoute}
          onClose={
            isGallery
              ? () => {
                  history.back();
                }
              : undefined
          }
        />
      ) : null}

      {hasOfferExpired ? (
        <>
          <div className="w-full px-4">
            {hotel ? (
              <div className="mb-4 flex w-full flex-col gap-4 rounded-3xl bg-white p-1 text-start">
                <div className="flex w-full justify-between">
                  <div className="flex min-h-28 w-[100px] shrink-0 items-center overflow-clip rounded-s-2xl bg-gray-100 text-center text-xs text-text-tertiary">
                    {hotel.mainImageUrl ? (
                      <Image
                        className="h-full w-full object-cover"
                        src={hotel.mainImageUrl}
                        alt={hotel.name}
                      />
                    ) : (
                      <span className="grow">{t("noImage")}</span>
                    )}
                  </div>

                  <div className="flex w-full flex-col p-3">
                    <span className="mb-2 line-clamp-2 text-base/snug font-medium text-text-title">
                      <HotelName name={hotel.name} />
                    </span>

                    <span className="text-sm text-semantic-error-strong">
                      {t("hotels.expired.heading")}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="section-title">{t("hotels.expired.listTitle")}</div>
          </div>

          <MayLikeHotelsList />
        </>
      ) : (
        <Outlet />
      )}
    </HotelOffersContextProvider>
  );
}
